import { SelectDropdown, Slider } from "@coval/ui"

import {
  clampVoiceSpeed,
  getVoiceSpeedRange,
  VOICE_SPEED_DEFAULT,
  VOICE_VOLUME_DEFAULT,
  VOICE_VOLUME_MAX,
  VOICE_VOLUME_MIN,
  VOICE_VOLUME_STEP,
  type CovalVoice,
} from "@/types/ModelConfigs/CovalVoiceConstants"

import { AudioPreviewButton } from "./AudioPreviewButton"
import { getVoiceAccentLabel } from "./personaVoiceLibraryUtils"

export const VOICE_SAMPLE_TOOLTIP =
  "Voice sample. Reflects the selected accent where a per-accent sample is available; otherwise this is a generic sample, but the selected language and accent are still applied when simulations run."

interface SelectedVoiceSummaryProps {
  voice: CovalVoice
  previewUrl?: string
  voiceVolume?: number
  voiceSpeed?: number
  isDisabled: boolean
  currentlyPlayingAudioId?: string | null
  onVoiceVolumeChange?: (volume: number | undefined) => void
  onVoiceSpeedChange?: (speed: number | undefined) => void
  onPlayingChange?: (playingId: string | null) => void
}

export function SelectedVoiceSummary({
  voice,
  previewUrl,
  voiceVolume,
  voiceSpeed,
  isDisabled,
  currentlyPlayingAudioId,
  onVoiceVolumeChange,
  onVoiceSpeedChange,
  onPlayingChange,
}: SelectedVoiceSummaryProps) {
  const accentLabel = getVoiceAccentLabel(voice)
  const effectiveVolume = voiceVolume ?? VOICE_VOLUME_DEFAULT
  const speedRange = getVoiceSpeedRange(voice.name)
  const effectiveSpeed =
    clampVoiceSpeed(voiceSpeed, voice.name) ?? VOICE_SPEED_DEFAULT

  return (
    <div
      className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6"
      role="group"
      aria-label={`Selected voice ${voice.displayName}`}
    >
      <div className="flex min-w-0 items-center gap-3 lg:w-64 lg:shrink-0">
        <AudioPreviewButton
          previewUrl={previewUrl}
          audioLabel={voice.displayName}
          volume={effectiveVolume}
          playbackRate={effectiveSpeed}
          currentlyPlayingId={currentlyPlayingAudioId}
          onPlayingChange={onPlayingChange}
          tooltipContent={VOICE_SAMPLE_TOOLTIP}
        />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {voice.displayName}
          </div>
          <div className="truncate text-xs text-coval-text-muted">
            {voice.voicePresentation}
            {accentLabel ? `, ${accentLabel}` : ""}
          </div>
        </div>
      </div>
      {/* The controls sit beside the voice once the band is wide enough, so the
          selection reads as one line rather than a stack. */}
      <div className="flex min-w-0 flex-1 flex-col gap-2 xl:flex-row xl:gap-6">
        <VoiceParameterSlider
          label="Volume"
          valueLabel={`${Math.round(effectiveVolume * 100)}%`}
          ariaLabel={`Voice volume for ${voice.displayName}`}
          value={effectiveVolume}
          minValue={VOICE_VOLUME_MIN}
          maxValue={VOICE_VOLUME_MAX}
          step={VOICE_VOLUME_STEP}
          defaultValue={VOICE_VOLUME_DEFAULT}
          isDisabled={isDisabled || !onVoiceVolumeChange}
          onChange={(next) => {
            if (!onVoiceVolumeChange) {
              return
            }
            onVoiceVolumeChange(
              next === VOICE_VOLUME_DEFAULT ? undefined : next,
            )
          }}
        />
        {speedRange && (
          <div className="flex shrink-0 items-center gap-3">
            <span className="w-14 shrink-0 text-xs font-medium text-coval-text-muted xl:w-auto">
              Speed
            </span>
            <SelectDropdown
              aria-label={`Voice speed for ${voice.displayName}`}
              options={getSpeedOptions(speedRange.presets, effectiveSpeed)}
              value={String(effectiveSpeed)}
              disabled={isDisabled || !onVoiceSpeedChange}
              onValueChange={(next) => {
                if (!onVoiceSpeedChange) {
                  return
                }
                const speed = Number(next)
                onVoiceSpeedChange(
                  speed === VOICE_SPEED_DEFAULT ? undefined : speed,
                )
              }}
              className="w-32"
            />
          </div>
        )}
      </div>
    </div>
  )
}

function formatSpeed(speed: number): string {
  const label = `${Number(speed.toFixed(2))}x`
  return speed === VOICE_SPEED_DEFAULT ? `${label} (Normal)` : label
}

/**
 * The provider's presets, plus the saved speed when it falls between them so
 * an existing persona keeps and shows its exact value.
 */
function getSpeedOptions(presets: number[], current: number) {
  const speeds = presets.includes(current)
    ? presets
    : [...presets, current].sort((a, b) => a - b)
  return speeds.map((speed) => ({
    value: String(speed),
    label: formatSpeed(speed),
  }))
}

function VoiceParameterSlider({
  label,
  valueLabel,
  ariaLabel,
  value,
  minValue,
  maxValue,
  step,
  defaultValue,
  isDisabled,
  onChange,
}: {
  label: string
  valueLabel: string
  ariaLabel: string
  value: number
  minValue: number
  maxValue: number
  step: number
  defaultValue: number
  isDisabled: boolean
  onChange: (value: number) => void
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <span className="w-14 shrink-0 text-xs font-medium text-coval-text-muted">
        {label}
      </span>
      <Slider
        aria-label={ariaLabel}
        value={[value]}
        min={minValue}
        max={maxValue}
        step={step}
        defaultValue={[defaultValue]}
        onValueChange={(next) => {
          onChange(next[0] ?? defaultValue)
        }}
        disabled={isDisabled}
        className="flex-1"
      />
      <span className="w-12 shrink-0 text-right text-xs font-medium tabular-nums">
        {valueLabel}
      </span>
    </div>
  )
}
